// Where a social post's picture and video come from. Pure (tested in test/social-media.test.js).
import type { SocialPost } from "./SocialViews";

/** The id in a TikTok video's address (https://www.tiktok.com/@name/video/<id>). */
export const tiktokId = (p: Pick<SocialPost, "platform" | "url">) =>
  p.platform === "tiktok" ? /^https:\/\/(?:www\.)?tiktok\.com\/@[\w.-]+\/video\/(\d{8,25})/i.exec(p.url || "")?.[1] ?? null : null;

/** A Facebook post's own address (https://…facebook.com/…), else null. */
const facebookUrl = (p: Pick<SocialPost, "platform" | "url">) =>
  p.platform === "facebook" && /^https:\/\/(?:www\.|m\.|web\.)?facebook\.com\/./i.test(p.url || "") ? p.url : null;

/** A post's picture through our origin. Saved CDN links expire: Instagram falls back to the
 *  shortcode, TikTok to the video's address and Facebook to the post's address (the server
 *  asks the platform for a fresh picture). */
export const mediaSrc = (p: Pick<SocialPost, "thumbnail_url" | "shortcode" | "platform" | "url">) => {
  const q = new URLSearchParams();
  if (p.thumbnail_url) q.set("u", p.thumbnail_url);
  if (p.platform === "instagram" && p.shortcode) q.set("sc", p.shortcode);
  if (tiktokId(p) && p.url) q.set("tt", p.url);
  if (facebookUrl(p)) q.set("fb", p.url!);
  const qs = q.toString();
  return qs ? `/api/competitors/media?${qs}` : null;
};

/** The platform's own player for a post (TikTok, Instagram reels, any Facebook post).
 *  Their play links are signed and die within days; the players keep working. */
export const embedSrc = (p: Pick<SocialPost, "platform" | "url" | "shortcode" | "format">): string | null => {
  const id = tiktokId(p);
  if (id) return `https://www.tiktok.com/player/v1/${id}?autoplay=1&music_info=1&description=0&rel=0`;
  if (p.platform === "instagram" && p.shortcode && /^[\w-]{5,40}$/.test(p.shortcode) && (p.format === "reel" || p.format === "video")) {
    return `https://www.instagram.com/reel/${p.shortcode}/embed/`;
  }
  // Facebook's post plugin shows the post itself (a video plays there; a photo stays fresh).
  const fb = facebookUrl(p);
  if (fb) return `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(fb)}&show_text=false&width=500`;
  return null;
};
